import { createRoot, type Root } from 'react-dom/client';
import App from './App';
import { baseUrl as _mfBaseUrl } from './baseUrl';
import { isValidJWT } from './components/Auth/JWTAuth';
import { fetchHostEntity } from './witboost/catalog';

/**
 * The host loads only the entry script, so the stylesheet has to be linked here.
 * It is a link rather than an injected <style> because Witboost's CSP carries a
 * nonce for styles, which blocks inline ones; an external sheet from our origin
 * is allowed by its `https:` source. The href comes from the asset manifest,
 * being content-hashed.
 */
async function loadStylesheet(): Promise<void> {
  if (import.meta.env.DEV) {
    return;
  }

  try {
    const manifest = await (await fetch(`${_mfBaseUrl}asset-manifest.json`)).json() as {
      files?: Record<string, string>;
    };
    const href = manifest.files?.['main.css'];

    if (!href || document.querySelector(`link[href="${_mfBaseUrl}${href.replace(/^\//, '')}"]`)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${_mfBaseUrl}${href.replace(/^\//, '')}`;
    document.head.appendChild(link);
  } catch (error) {
    console.warn('[Style] Could not load the stylesheet', error);
  }
}

/**
 * Runtime configuration is authenticated, so it is fetched rather than loaded as
 * a script tag: a tag cannot carry the bearer token.
 */
async function loadEnv(token: string): Promise<boolean> {
  try {
    const response = await fetch(`${_mfBaseUrl}api/env`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      console.warn('[Env] Runtime configuration request failed with', response.status);
      return false;
    }

    (window as unknown as { env: Record<string, string> }).env = await response.json() as Record<string, string>;
    return true;
  } catch (error) {
    console.warn('[Env] Could not reach the runtime configuration endpoint', error);
    return false;
  }
}

// Witboost hands us its Material UI theme object. We never construct MUI with it,
// we only read tokens off it, so it stays untyped structural data here.
export type WitboostTheme = Record<string, unknown>;

export type MicroFrontendContract = {
  containerId: string;
  theme?: WitboostTheme;
  token: string;
  parameters?: {
    // Configured on the Witboost page with valuePath: entity. Witboost does not
    // currently deliver it on a systemPage, hence the catalog fallback below.
    entity?: unknown;
  };
};

declare global {
  interface Window {
    renderOntologyEditor: (params: MicroFrontendContract) => void;
    unmountOntologyEditor: (containerId: string) => void;
  }
}

const roots = new Map<string, Root>();

// No StrictMode: Cytoscape does not survive double-invoked effects.
function renderToContainer(containerId: string, content: React.ReactNode) {
  const element = document.getElementById(containerId);

  if (!element) {
    console.error('[Render] Container not found:', containerId);
    return;
  }

  let root = roots.get(containerId);

  if (!root) {
    root = createRoot(element);
    roots.set(containerId, root);
  }

  root.render(content);
}

// The dev harness has no Witboost to be handed a token by, so it asks the local
// issuer for one. The token is then validated normally: no path is skipped, and
// Vite strips this in production builds.
//
// The issuer address is a constant here rather than runtime configuration,
// because configuration is what we need the token to fetch.
async function getDevToken(): Promise<string> {
  const issuer = import.meta.env.VITE_DEV_ISSUER ?? 'http://localhost:9000/api/auth';

  try {
    const response = await fetch(`${issuer}/dev-token`);
    return ((await response.json()) as { token: string }).token;
  } catch {
    console.warn('[Render] Local dev issuer unreachable. Is `npm run dev` running?');
    return '';
  }
}

window.renderOntologyEditor = async (params) => {
  const token = params.token || (import.meta.env.DEV ? await getDevToken() : '');
  const [, configured] = await Promise.all([loadStylesheet(), loadEnv(token)]);
  const validToken = configured && await isValidJWT(token);

  if (!validToken) {
    console.warn('[Render] Invalid JWT token');
    renderToContainer(
      params.containerId,
      <div className="wb-ontology-editor">Authentication failed.</div>,
    );
    return;
  }

  renderToContainer(
    params.containerId,
    <App
      theme={params.theme}
      entity={params.parameters?.entity ?? (await fetchHostEntity(token))}
      token={token}
    />,
  );
};

window.unmountOntologyEditor = (containerId) => {
  const root = roots.get(containerId);

  if (root) {
    root.unmount();
    roots.delete(containerId);
  }
};
